"use client";

import { Button } from "./Button";

type PaginationProps = {
  /** 0-based page index. */
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  /** Disable controls while a fetch is in flight. */
  disabled?: boolean;
};

/**
 * Shared list pagination: result range, "Page X of Y", and prev/next.
 * Collapses to a plain result count when everything fits on one page.
 */
export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  disabled = false,
}: PaginationProps) {
  if (total <= pageSize) {
    return (
      <p className="mt-3 text-xs text-gray-500">
        {total === 0
          ? "0 results"
          : `${total} ${total === 1 ? "result" : "results"}`}
      </p>
    );
  }

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const from = currentPage * pageSize + 1;
  const to = Math.min((currentPage + 1) * pageSize, total);
  const hasPrev = currentPage > 0;
  const hasNext = currentPage < pageCount - 1;

  return (
    <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-xs text-gray-500">
        Showing {from}–{to} of {total} · Page {currentPage + 1} of {pageCount}
      </p>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={disabled || !hasPrev}
          onClick={() => onPageChange(currentPage - 1)}
        >
          Previous
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={disabled || !hasNext}
          onClick={() => onPageChange(currentPage + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
